import { Box, IconButton, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { neutral } from '@ahora-patagonia/design-tokens';
import {
  IconoCompartir,
  IconoCopiar,
  IconoFacebook,
  IconoInstagram,
  IconoWhatsApp,
  IconoX,
} from '../../../shared/components/iconos.js';

export interface BotonesCompartirProps {
  /** URL **absoluta** de la nota (la arma la vista con el origen del sitio, `useSitio`). */
  url: string;
  titulo: string;
}

/** Cuánto queda el aviso de copiado a la vista. */
const AVISO_MS = 2000;

/**
 * Compartir: botones de 48×48, arriba bajo la firma y repetidos al final del cuerpo.
 * **Nada flota**: los botones de compartir no son una barra fija (handoff §2).
 *
 * Abre la fila el botón de compartir, que llama al diálogo del sistema: es el que llega a lo que no
 * tiene botón propio (Telegram, el mail, el bloc de notas). Solo se dibuja donde el navegador lo
 * tiene, porque un botón que no abre nada es peor que no tenerlo.
 *
 * WhatsApp, Facebook y X son links `href` puros. Compartir, Instagram y "copiar" llevan JS, y los
 * tres avisan con un texto —el color del ícono solo no comunica— que se anuncia por `aria-live` y
 * se dibuja en posición absoluta para no correr los botones al aparecer.
 *
 * **Instagram no puede recibir el enlace solo**: no publica una URL de compartir, porque no admite
 * enlaces en las publicaciones. Así que su botón hace lo único que se puede hacer de verdad —copia
 * el enlace y abre Instagram— y el aviso dice exactamente eso, para que el lector sepa que le queda
 * pegarlo en su historia. Prometer menos y cumplirlo es mejor que un ícono que no lleva a ninguna
 * parte.
 */
export function BotonesCompartir({ url, titulo }: BotonesCompartirProps) {
  const [aviso, setAviso] = useState('');
  /**
   * Si el navegador tiene el diálogo nativo. Se resuelve en un efecto y arranca en `false` a
   * propósito: `navigator` no existe en el server, y mirarlo en el render dejaría el HTML del SSR
   * distinto del primer render del cliente (mismatch de hidratación). Un botón que aparece un
   * instante después es preferible a que React descarte el árbol entero.
   */
  const [puedeCompartir, setPuedeCompartir] = useState(false);
  const temporizador = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const texto = encodeURIComponent(`${titulo} ${url}`);
  const destino = encodeURIComponent(url);

  useEffect(() => () => clearTimeout(temporizador.current), []);
  useEffect(() => setPuedeCompartir(typeof navigator.share === 'function'), []);

  // El parámetro NO se llama `texto`: así se llama el mensaje ya codificado para WhatsApp, unas
  // líneas más arriba, y taparlo acá era pedir un bug.
  function avisar(mensaje: string): void {
    setAviso(mensaje);
    clearTimeout(temporizador.current);
    temporizador.current = setTimeout(() => setAviso(''), AVISO_MS);
  }

  /** Al portapapeles, y si no se puede avisa. Lo comparten "copiar" e Instagram. */
  async function alPortapapeles(): Promise<boolean> {
    // Sin portapapeles (http sin localhost, navegador viejo) `navigator.clipboard` es
    // `undefined`: encadenar un `.then()` ahí explotaba en silencio y el lector no se
    // enteraba de nada.
    try {
      if (!navigator.clipboard) return false;
      await navigator.clipboard.writeText(url);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * El diálogo del sistema, que lista las apps del teléfono. Es el camino a todo lo que no tiene
   * botón propio acá: Telegram, el mail, las notas.
   */
  async function compartir(): Promise<void> {
    try {
      await navigator.share({ title: titulo, url });
    } catch (err) {
      // Cerrar el diálogo sin elegir nada tira `AbortError`, y no es un error del lector: avisarle
      // "no se pudo" cuando fue él el que canceló es peor que no decir nada.
      if (err instanceof Error && err.name === 'AbortError') return;
      avisar('No se pudo compartir');
    }
  }

  /**
   * Instagram: copiar el enlace y abrir la app. Es todo lo que se puede hacer —no hay URL de
   * compartir— y el aviso lo dice, así el lector sabe que le falta pegarlo en su historia.
   *
   * Se abre igual si el copiado falla: quedarse sin hacer nada sería peor, y con el enlace a la
   * vista en la barra todavía puede copiarlo a mano.
   */
  async function aInstagram(): Promise<void> {
    const ok = await alPortapapeles();
    avisar(ok ? 'Enlace copiado: pegalo en tu historia' : 'Abrimos Instagram: copiá el enlace');
    window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');
  }

  async function copiar(): Promise<void> {
    avisar((await alPortapapeles()) ? '¡Copiado!' : 'No se pudo copiar');
  }

  const copiado = aviso === '¡Copiado!';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }} aria-label="Compartir">
      {/* Solo donde el navegador lo tiene: en un escritorio sin `navigator.share` un botón que no
          abre nada es peor que no tenerlo, y ahí quedan los otros cinco para el mismo trabajo. */}
      {puedeCompartir && (
        <IconButton aria-label="Compartir con otra aplicación" onClick={() => void compartir()}>
          <IconoCompartir />
        </IconButton>
      )}
      <IconButton
        component="a"
        href={`https://wa.me/?text=${texto}`}
        target="_blank"
        rel="noopener"
        aria-label="Compartir por WhatsApp"
      >
        <IconoWhatsApp />
      </IconButton>
      <IconButton
        component="a"
        href={`https://www.facebook.com/sharer/sharer.php?u=${destino}`}
        target="_blank"
        rel="noopener"
        aria-label="Compartir en Facebook"
      >
        <IconoFacebook />
      </IconButton>
      <IconButton
        component="a"
        href={`https://x.com/intent/post?url=${destino}&text=${encodeURIComponent(titulo)}`}
        target="_blank"
        rel="noopener"
        aria-label="Compartir en X"
      >
        <IconoX />
      </IconButton>
      <IconButton aria-label="Compartir en Instagram" onClick={() => void aInstagram()}>
        <IconoInstagram />
      </IconButton>
      <IconButton
        aria-label={copiado ? 'Dirección copiada' : 'Copiar la dirección'}
        onClick={() => void copiar()}
        sx={copiado ? { color: 'var(--color-cliente)' } : undefined}
      >
        <IconoCopiar />
      </IconButton>

      {/*
        El nodo vive siempre y solo cambia su texto: montarlo junto con el aviso hace que
        varios lectores de pantalla no lo anuncien. Absoluto y sin envolver, para que
        aparecer no empuje los botones ni un píxel.
      */}
      <Typography
        variant="firma"
        component="span"
        role="status"
        aria-live="polite"
        sx={{
          position: 'absolute',
          right: 0,
          top: '100%',
          whiteSpace: 'nowrap',
          color: copiado ? 'var(--color-cliente)' : neutral[700],
          pointerEvents: 'none',
        }}
      >
        {aviso}
      </Typography>
    </Box>
  );
}
